
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  BookOpen, 
  Package, 
  ShoppingCart, 
  Clock, 
  CheckCircle, 
  
  Plus,
  Edit,
  Eye,
  Truck,
  ArrowLeftRight,
  MinusCircle,
  Mail,
  Bell,
  Users,
  CreditCard,
  Settings,
  Database,
  BarChart3,
  FileText,
  Calendar,
  MapPin,
  Phone,
  Globe,
  Shield,
  Zap,
  RefreshCw,
  TrendingUp,
  Filter,
  Search,
  Download,
  Upload,
  Printer,
  Tag,
  Star,
  Heart,
  MessageSquare,
  HelpCircle,
  Info,
  ExternalLink,
  UserPlus,
  User
} from "lucide-react";

export default function Help() {
  const { t } = useTranslation('help');

  return (
    <div className="min-h-screen gradient-bg p-6">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            <BookOpen className="inline-block w-10 h-10 mr-3 text-amber-600" />
            {t('title')}
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            {t('subtitle')}
          </p>
        </div>

        {/* Navigation */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="elegant-card cursor-pointer hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-700">
                <Package className="w-5 h-5" />
                {t('navigation.warehouse_operations')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">{t('navigation.warehouse_description')}</p>
            </CardContent>
          </Card>

          <Card className="elegant-card cursor-pointer hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <ShoppingCart className="w-5 h-5" />
                {t('navigation.orders_sales')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">{t('navigation.orders_description')}</p>
            </CardContent>
          </Card>

          <Card className="elegant-card cursor-pointer hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-700">
                <Bell className="w-5 h-5" />
                {t('navigation.automation')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600">{t('navigation.automation_description')}</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="space-y-12">
          
          {/* Складские накладные */}
          <section id="warehouse-operations">
            <Card className="elegant-card">
              <CardHeader>
                <CardTitle className="text-2xl text-slate-900 flex items-center gap-3">
                  <Package className="w-7 h-7 text-amber-600" />
                  {t('warehouse_operations.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                
                {/* Текущее состояние */}
                <div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    {t('warehouse_operations.current_state.title')}
                  </h3>
                  
                  <div className="grid md:grid-cols-1 gap-6">
                    <div>
                      <h4 className="font-semibold text-green-700 mb-3">{t('warehouse_operations.current_state.implemented.title')}</h4>
                      <ul className="space-y-2 text-slate-600">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {t('warehouse_operations.current_state.implemented.manual_invoices')}
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {t('warehouse_operations.current_state.implemented.balance_system')}
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {t('warehouse_operations.current_state.implemented.view_edit')}
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {t('warehouse_operations.current_state.implemented.history')}
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {t('warehouse_operations.current_state.implemented.processing')}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Типы накладных */}
                <div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4">{t('warehouse_operations.invoice_types.title')}</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="border-l-4 border-l-green-500">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Plus className="w-5 h-5 text-green-600" />
                          <Badge className="bg-green-100 text-green-800">{t('warehouse_operations.invoice_types.receipt.title')}</Badge>
                        </div>
                        <p className="text-sm text-slate-600">{t('warehouse_operations.invoice_types.receipt.description')}</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-blue-500">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <ArrowLeftRight className="w-5 h-5 text-blue-600" />
                          <Badge className="bg-blue-100 text-blue-800">{t('warehouse_operations.invoice_types.transfer.title')}</Badge>
                        </div>
                        <p className="text-sm text-slate-600">{t('warehouse_operations.invoice_types.transfer.description')}</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-orange-500">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Truck className="w-5 h-5 text-orange-600" />
                          <Badge className="bg-orange-100 text-orange-800">{t('warehouse_operations.invoice_types.shipment.title')}</Badge>
                        </div>
                        <p className="text-sm text-slate-600">{t('warehouse_operations.invoice_types.shipment.description')}</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-red-500">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <MinusCircle className="w-5 h-5 text-red-600" />
                          <Badge className="bg-red-100 text-red-800">{t('warehouse_operations.invoice_types.write_off.title')}</Badge>
                        </div>
                        <p className="text-sm text-slate-600">{t('warehouse_operations.invoice_types.write_off.description')}</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Логика работы и создания накладных */}
                <div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4">{t('warehouse_operations.logic.title')}</h3>
                  <Card className="bg-slate-50 border-slate-200">
                    <CardContent className="p-6 space-y-4">
                      <ul className="list-disc pl-6 space-y-2 text-slate-700">
                        <li>{t('warehouse_operations.logic.rules.transfer_pair')}</li>
                        <li>{t('warehouse_operations.logic.rules.print_run_link')}</li>
                        <li>{t('warehouse_operations.logic.rules.order_link')}</li>
                        <li>{t('warehouse_operations.logic.rules.basis_field')}</li>
                        <li>{t('warehouse_operations.logic.rules.processing')}</li>
                      </ul>
                      <div>
                        <h4 className="font-semibold text-slate-800 mb-2">{t('warehouse_operations.logic.creation.manual.title')}</h4>
                        <ol className="list-decimal list-inside space-y-1 text-slate-600">
                          {t('warehouse_operations.logic.creation.manual.steps', { returnObjects: true }).map((step, index) => (
                            <li key={index}>{step}</li>
                          ))}
                        </ol>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Связь тиражей и накладных */}
                <div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Package className="w-5 h-5 text-amber-600" />
                    {t('warehouse_operations.print_run_integration.title')}
                  </h3>
                  
                  <Card className="bg-slate-50 border-slate-200">
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-amber-600 font-bold">1</span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-800">{t('warehouse_operations.print_run_integration.step1.title')}</h4>
                            <p className="text-slate-600">{t('warehouse_operations.print_run_integration.step1.description')}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-blue-600 font-bold">2</span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-800">{t('warehouse_operations.print_run_integration.step2.title')}</h4>
                            <p className="text-slate-600">{t('warehouse_operations.print_run_integration.step2.description')}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-green-600 font-bold">3</span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-800">{t('warehouse_operations.print_run_integration.step3.title')}</h4>
                            <p className="text-slate-600">
                              <strong>{t('warehouse_operations.print_run_integration.step3.description')}</strong>
                            </p>
                            <ul className="text-sm text-slate-500 mt-2 ml-4 space-y-1">
                              <li>• {t('warehouse_operations.print_run_integration.step3.type')}</li>
                              <li>• {t('warehouse_operations.print_run_integration.step3.warehouse')}</li>
                              <li>• {t('warehouse_operations.print_run_integration.step3.goods')}</li>
                              <li>• {t('warehouse_operations.print_run_integration.step3.quantity')}</li>
                              <li>• {t('warehouse_operations.print_run_integration.step3.basis')}</li>
                            </ul>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-purple-600 font-bold">4</span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-800">{t('warehouse_operations.print_run_integration.step4.title')}</h4>
                            <p className="text-slate-600">{t('warehouse_operations.print_run_integration.step4.description')}</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Инструкции по использованию */}
                <div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4">{t('warehouse_operations.usage_instructions.title')}</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <Card>
                      <CardContent className="p-6">
                        <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                          <Plus className="w-5 h-5 text-green-600" />
                          {t('warehouse_operations.usage_instructions.creation.title')}
                        </h4>
                        <ol className="list-decimal list-inside space-y-1 text-slate-600">
                          {t('warehouse_operations.usage_instructions.creation.steps', { returnObjects: true }).map((step, index) => (
                            <li key={index}>{step}</li>
                          ))}
                        </ol>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="p-6">
                        <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                          <Eye className="w-5 h-5 text-blue-600" />
                          {t('warehouse_operations.usage_instructions.document_work.title')}
                        </h4>
                        <ul className="space-y-1 text-slate-600">
                          <li className="flex items-center gap-2">
                            <Eye className="w-4 h-4 text-blue-500" />
                            <span>{t('warehouse_operations.usage_instructions.document_work.view')}</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <Edit className="w-4 h-4 text-amber-500" />
                            <span>{t('warehouse_operations.usage_instructions.document_work.edit')}</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span>{t('warehouse_operations.usage_instructions.document_work.process')}</span>
                          </li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                
              </CardContent>
            </Card>
          </section>

          {/* Заказы и продажи */}
          <section id="orders-sales">
            <Card className="elegant-card">
              <CardHeader>
                <CardTitle className="text-2xl text-slate-900 flex items-center gap-3">
                  <ShoppingCart className="w-7 h-7 text-green-600" />
                  {t('orders_sales.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                
                {/* Жизненный цикл заказа */}
                <div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-blue-600" />
                    {t('orders_sales.order_lifecycle.title')}
                  </h3>
                  
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="border-l-4 border-l-blue-500">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Plus className="w-5 h-5 text-blue-600" />
                          <Badge className="bg-blue-100 text-blue-800">{t('orders_sales.order_lifecycle.created')}</Badge>
                        </div>
                        <p className="text-sm text-slate-600">Заказ создан в системе</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-green-500">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <CreditCard className="w-5 h-5 text-green-600" />
                          <Badge className="bg-green-100 text-green-800">{t('orders_sales.order_lifecycle.paid')}</Badge>
                        </div>
                        <p className="text-sm text-slate-600">Заказ оплачен клиентом</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-purple-500">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="w-5 h-5 text-purple-600" />
                          <Badge className="bg-purple-100 text-purple-800">{t('orders_sales.order_lifecycle.shipped')}</Badge>
                        </div>
                        <p className="text-sm text-slate-600">Заказ отправлен клиенту</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Управление клиентами */}
                <div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-600" />
                    {t('orders_sales.customer_management.title')}
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <Card>
                      <CardContent className="p-6">
                        <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                          <UserPlus className="w-5 h-5 text-green-600" />
                          {t('orders_sales.customer_management.registration')}
                        </h4>
                        <ul className="space-y-2 text-slate-600">
                          <li>• Регистрация новых клиентов</li>
                          <li>• Верификация email адресов</li>
                          <li>• Создание профилей клиентов</li>
                        </ul>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="p-6">
                        <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                          <User className="w-5 h-5 text-blue-600" />
                          {t('orders_sales.customer_management.profiles')}
                        </h4>
                        <ul className="space-y-2 text-slate-600">
                          <li>• Управление контактной информацией</li>
                          <li>• История заказов клиента</li>
                          <li>• Предпочтения и настройки</li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Платежная система */}
                <div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-green-600" />
                    {t('orders_sales.payment_system.title')}
                  </h3>
                  
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold text-green-800 mb-3">{t('orders_sales.payment_system.methods')}</h4>
                          <ul className="space-y-2 text-green-700">
                            <li>• Банковские карты (Visa, MasterCard)</li>
                            <li>• Электронные кошельки</li>
                            <li>• Банковские переводы</li>
                            <li>• Наложенный платеж</li>
                          </ul>
                        </div>
                        
                        <div>
                          <h4 className="font-semibold text-green-800 mb-3">{t('orders_sales.payment_system.processing')}</h4>
                          <ul className="space-y-2 text-green-700">
                            <li>• Автоматическая обработка платежей</li>
                            <li>• Безопасное хранение данных</li>
                            <li>• Интеграция с платежными системами</li>
                            <li>• Обработка возвратов</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Автоматизация */}
          <section id="automation">
            <Card className="elegant-card">
              <CardHeader>
                <CardTitle className="text-2xl text-slate-900 flex items-center gap-3">
                  <Zap className="w-7 h-7 text-blue-600" />
                  {t('automation.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                
                {/* Уведомления */}
                <div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Bell className="w-5 h-5 text-amber-600" />
                    {t('automation.notifications.title')}
                  </h3>
                  
                  <div className="grid md:grid-cols-3 gap-4">
                    <Card className="border-l-4 border-l-blue-500">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Mail className="w-5 h-5 text-blue-600" />
                          <Badge className="bg-blue-100 text-blue-800">{t('automation.notifications.email')}</Badge>
                        </div>
                        <p className="text-sm text-slate-600">Автоматические email уведомления о статусе заказов</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-green-500">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <MessageSquare className="w-5 h-5 text-green-600" />
                          <Badge className="bg-green-100 text-green-800">{t('automation.notifications.sms')}</Badge>
                        </div>
                        <p className="text-sm text-slate-600">SMS уведомления для важных событий</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-l-4 border-l-purple-500">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Bell className="w-5 h-5 text-purple-600" />
                          <Badge className="bg-purple-100 text-purple-800">{t('automation.notifications.in_app')}</Badge>
                        </div>
                        <p className="text-sm text-slate-600">Внутренние уведомления в системе</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Автоматизированные процессы */}
                <div>
                  <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Settings className="w-5 h-5 text-indigo-600" />
                    {t('automation.workflows.title')}
                  </h3>
                  
                  <div className="space-y-4">
                    <Card className="bg-indigo-50 border-indigo-200">
                      <CardContent className="p-6">
                        <h4 className="font-bold text-indigo-800 mb-3 flex items-center gap-2">
                          <ShoppingCart className="w-5 h-5" />
                          {t('automation.workflows.order_processing')}
                        </h4>
                        <ul className="space-y-2 text-indigo-700">
                          <li>• Автоматическое создание накладных при заказах</li>
                          <li>• Резервирование товаров</li>
                          <li>• Обновление статусов заказов</li>
                          <li>• Уведомления клиентов</li>
                        </ul>
                      </CardContent>
                    </Card>

                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="p-6">
                        <h4 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                          <Package className="w-5 h-5" />
                          {t('automation.workflows.inventory_management')}
                        </h4>
                        <ul className="space-y-2 text-blue-700">
                          <li>• Автоматическое обновление остатков</li>
                          <li>• Создание накладных при поступлении тиражей</li>
                          <li>• Проверка листа ожидания</li>
                          <li>• Уведомления о низких остатках</li>
                        </ul>
                      </CardContent>
                    </Card>

                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="p-6">
                        <h4 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                          <Users className="w-5 h-5" />
                          {t('automation.workflows.customer_communication')}
                        </h4>
                        <ul className="space-y-2 text-green-700">
                          <li>• Приветственные письма новым клиентам</li>
                          <li>• Уведомления о статусе заказов</li>
                          <li>• Напоминания о неоплаченных заказах</li>
                          <li>• Уведомления о поступлении ожидаемых книг</li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>

        
      </div>
    </div>
  );
}
